import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
interface Review {
  id: string;
  name: string;
  avatar: string;
  location: string;
  rating: number;
  text: string;
  date: string;
  hotel: string;
}
const reviews: Review[] = [{
  id: '1',
  name: 'Sarah Mitchell',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&q=80',
  location: 'New York, USA',
  rating: 5,
  text: 'Absolutely incredible experience! Travel Sewa made booking our Dubai trip so seamless. The hotel recommendations were spot-on, and the customer service was exceptional.',
  date: 'December 2024',
  hotel: 'Burj Al Arab Jumeirah'
}, {
  id: '2',
  name: 'Rajesh Sharma',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&q=80',
  location: 'Mumbai, India',
  rating: 5,
  text: "Best travel booking platform I've used. Found amazing deals for our family trip to Kathmandu. The heritage hotel they suggested was beyond our expectations!",
  date: 'November 2024',
  hotel: "Dwarika's Hotel"
}, {
  id: '3',
  name: 'Emma Laurent',
  avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&q=80',
  location: 'London, UK',
  rating: 5,
  text: 'Travel Sewa helped us plan our honeymoon in Paris perfectly. The hotel had stunning Eiffel Tower views exactly as described. Will definitely use again!',
  date: 'October 2024',
  hotel: 'Hotel Plaza Athénée'
}, {
  id: '4',
  name: 'Ahmed Hassan',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&q=80',
  location: 'Cairo, Egypt',
  rating: 5,
  text: 'The 24-hour support really came through when we needed to change our booking last minute. Professional, helpful, and genuinely caring service.',
  date: 'September 2024',
  hotel: 'The Oberoi New Delhi'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export function ReviewsSection() {
  return <section className="py-16 lg:py-24 bg-travel-blue-50" data-id="element-214">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-215">
        {/* Section Header */}
        <div className="text-center mb-12" data-id="element-216">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-217">
            What Our Customers Say
          </motion.h2>
          <motion.div initial={{
          scaleX: 0
        }} whileInView={{
          scaleX: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2,
          duration: 0.5
        }} className="w-20 h-1 bg-travel-blue-600 mx-auto rounded-full" data-id="element-218" />
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-219">
            Real experiences from travelers who booked with Travel Sewa
          </motion.p>
        </div>

        {/* Reviews Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-id="element-220">
          {reviews.map(review => <motion.article key={review.id} variants={itemVariants} className="bg-white rounded-2xl p-6 lg:p-8 shadow-sm hover:shadow-lg transition-shadow relative" data-id="element-221">
              {/* Quote Icon */}
              <div className="absolute top-6 right-6 text-travel-blue-100" data-id="element-222">
                <Quote className="w-12 h-12" data-id="element-223" />
              </div>

              {/* Rating */}
              <div className="flex gap-1 mb-4" data-id="element-224">
                {[...Array(5)].map((_, i) => <Star key={i} className={`w-5 h-5 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} data-id="element-225" />)}
              </div>

              {/* Review Text */}
              <p className="text-gray-700 mb-6 leading-relaxed relative z-10" data-id="element-226">
                "{review.text}"
              </p>

              {/* Hotel Badge */}
              <div className="inline-block px-3 py-1 bg-travel-blue-50 text-travel-blue-600 text-sm font-medium rounded-full mb-6" data-id="element-227">
                {review.hotel}
              </div>

              {/* Reviewer Info */}
              <div className="flex items-center gap-4 pt-4 border-t border-gray-100" data-id="element-228">
                <img src={review.avatar} alt={review.name} className="w-12 h-12 rounded-full object-cover" data-id="element-229" />
                <div data-id="element-230">
                  <h4 className="font-semibold text-gray-900" data-id="element-231">{review.name}</h4>
                  <p className="text-sm text-gray-500" data-id="element-232">
                    {review.location} • {review.date}
                  </p>
                </div>
              </div>
            </motion.article>)}
        </motion.div>

        {/* Trust Stats */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 text-center" data-id="element-233">
          {[{
          value: '50K+',
          label: 'Happy Travelers'
        }, {
          value: '4.8',
          label: 'Average Rating'
        }, {
          value: '10K+',
          label: 'Hotels Listed'
        }, {
          value: '24/7',
          label: 'Customer Support'
        }].map(stat => <div key={stat.label} className="bg-white rounded-xl p-6 shadow-sm" data-id="element-234">
              <div className="text-3xl font-bold text-travel-blue-600 mb-1" data-id="element-235">
                {stat.value}
              </div>
              <div className="text-sm text-gray-600" data-id="element-236">{stat.label}</div>
            </div>)}
        </motion.div>
      </div>
    </section>;
}